import React, { useEffect } from 'react';
import { DroneListProps } from './types';
import { ArcPagination, ArcSkeleton, ArcStack } from '@src/core/ui';
import { ArcDrone } from '@src/core/components';
import { useDispatch, useSelector } from 'react-redux';
import { selectDrone, setSelectedDrone } from '@src/features/fleet/pages/drones-page/redux';
import { DroneEntity } from '@src/core/features';
import { usePaginatedDrones } from '@src/features/fleet/pages/drones-page/hooks';
import { useGetDronesQuery } from '@src/core/features/drone/redux';
import { PAGINATION_DEFAULT_SIZE } from '@src/core/config';

const filterDronesBySearch = (drones: DroneEntity[], q: string): DroneEntity[] => {
  if (!q) return drones;
  const lower = q.trim().toLowerCase();
  return drones.filter((drone) => {
    const nameMatch = drone.name?.toLowerCase().includes(lower) ?? false;
    const serialMatch = drone.serialNumber?.toLowerCase().includes(lower) ?? false;
    return nameMatch || serialMatch;
  });
};

const DroneList: React.FC<DroneListProps> = ({ searchName = '' }) => {
  const dispatch = useDispatch();
  const selectedDrone = useSelector(selectDrone);
  const [searchPage, setSearchPage] = React.useState(0);
  const { drones, isLoading, pagination } = usePaginatedDrones({
    initialPage: 0,
    initialSize: PAGINATION_DEFAULT_SIZE,
  });
  const { data: allDronesData, isLoading: isLoadingAll } = useGetDronesQuery(undefined, {
    skip: !searchName.trim(),
  });

  const isSearchMode = Boolean(searchName.trim());
  const allDrones = allDronesData?.data ?? [];
  const filteredDrones = React.useMemo(() => {
    if (!isSearchMode) return drones;
    return filterDronesBySearch(allDrones, searchName);
  }, [isSearchMode, searchName, drones, allDrones]);

  const totalFilteredCount = filteredDrones.length;
  const searchTotalPages = Math.ceil(totalFilteredCount / PAGINATION_DEFAULT_SIZE) || 0;
  const paginatedFilteredDrones = React.useMemo(() => {
    if (!isSearchMode) return filteredDrones;
    const start = searchPage * PAGINATION_DEFAULT_SIZE;
    return filteredDrones.slice(start, start + PAGINATION_DEFAULT_SIZE);
  }, [isSearchMode, filteredDrones, searchPage]);

  const displayDrones = isSearchMode ? paginatedFilteredDrones : filteredDrones;
  const totalCount = isSearchMode ? totalFilteredCount : pagination.totalCount;
  const totalPages = isSearchMode
    ? searchTotalPages
    : Math.ceil(pagination.totalCount / PAGINATION_DEFAULT_SIZE) || 0;
  const currentPage = isSearchMode ? searchPage : pagination.page;
  const showPagination = totalCount > 0 && totalPages > 1;

  useEffect(() => {
    setSearchPage(0);
  }, [searchName]);

  useEffect(() => {
    if (drones.length > 0 && !selectedDrone && !isSearchMode) {
      dispatch(setSelectedDrone(drones[0]));
    }
    if (isSearchMode && paginatedFilteredDrones.length > 0 && !selectedDrone) {
      dispatch(setSelectedDrone(paginatedFilteredDrones[0]));
    }
  }, [drones.length, isSearchMode, paginatedFilteredDrones, selectedDrone, dispatch]);

  const onToggleDrone = (drone: DroneEntity) => {
    if (selectedDrone?.id === drone.id) {
      dispatch(setSelectedDrone(null));
      return;
    }
    dispatch(setSelectedDrone(drone));
  };

  const handlePageChange = React.useCallback(
    (_: unknown, page: number) => {
      if (isSearchMode) {
        setSearchPage(page - 1);
      } else {
        pagination.onPageChange(page - 1);
      }
    },
    [isSearchMode, pagination]
  );

  const isLoadingList = isSearchMode ? isLoadingAll : isLoading;
  if (isLoadingList) {
    return (
      <ArcStack spacing={1}>
        <ArcSkeleton height={80} />
        <ArcSkeleton height={80} />
        <ArcSkeleton height={80} />
        <ArcSkeleton height={80} />
      </ArcStack>
    );
  }

  return (
    <ArcStack sx={{ flex: 1, minHeight: 0 }}>
      <ArcStack
        spacing={1}
        sx={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        {displayDrones.map((drone) => (
          <ArcDrone.primary 
            key={drone.id} 
            drone={drone}
            selected={selectedDrone?.id === drone.id}
            onClick={() => onToggleDrone(drone)} />
        ))}
      </ArcStack>
      
      {showPagination && (
        <ArcStack alignItems="center" mt={2}>
          <ArcPagination
            page={currentPage + 1}
            count={totalPages}
            onChange={handlePageChange}
          />
        </ArcStack>
      )}
    </ArcStack>
  );
};

export default DroneList;